import { useMutation, useQuery, useQueryClient, UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import { enum_api_uri } from "../utils/enum";
import axiosInstance from "./axiosInstance";

// 모닝택배 휴무일 관리 — kind: off(종일휴무) / early(조기종료), source: api(공휴일) / manual(수동)
export type HolidayKind = "off" | "early";

export interface Holiday {
    holiday_no: number;
    holiday_date: string; // YYYY-MM-DD
    name: string;
    kind: HolidayKind;
    end_time: string | null; // HH:MM (early 전용)
    source: "api" | "manual";
}

export interface HolidayListResponse {
    year: number;
    month: number;
    default_end_time: string;
    public_holiday_count: number; // 해당 연도 공휴일(api) 보유 건수
    data_list: Holiday[];
    deleted_holidays: DeletedHoliday[]; // 해당 월의 삭제된 공휴일 — 복원 대상
}

export interface DeletedHoliday {
    holiday_no: number;
    holiday_date: string;
    name: string;
}

export interface HolidayBody {
    name: string;
    kind: HolidayKind;
    end_time: string | null;
}

export interface HolidaySyncResult {
    year: number;
    fetched: number;
    inserted: number;
    skipped: number;
}

const HOLIDAYS_KEY = "useGetConsoleHolidays";

// 백엔드 안내 메시지(중복·과거일·주말 등)를 그대로 노출
const toError = (error: any, fallback: string) => new Error(error?.response?.data?.message || error?.message || fallback);

export const useGetConsoleHolidays = (year: number, month: number): UseQueryResult<HolidayListResponse, Error> => {
    return useQuery({
        queryKey: [HOLIDAYS_KEY, year, month],
        queryFn: async () => {
            const response = await axiosInstance.get(enum_api_uri.get_console_holidays, { params: { year, month } });
            return response.data.data;
        },
    });
};

export const usePostConsoleHoliday = (): UseMutationResult<any, Error, HolidayBody & { holiday_date: string }> => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (body: HolidayBody & { holiday_date: string }) => {
            try {
                const response = await axiosInstance.post(enum_api_uri.post_console_holiday, body);
                return response.data;
            } catch (error: any) {
                throw toError(error, "등록에 실패했습니다.");
            }
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: [HOLIDAYS_KEY] }),
    });
};

export const usePutConsoleHoliday = (): UseMutationResult<any, Error, HolidayBody & { holiday_no: number }> => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ holiday_no, ...body }: HolidayBody & { holiday_no: number }) => {
            const url = enum_api_uri.put_console_holiday.replace(":holiday_no", String(holiday_no));
            try {
                const response = await axiosInstance.put(url, body);
                return response.data;
            } catch (error: any) {
                throw toError(error, "수정에 실패했습니다.");
            }
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: [HOLIDAYS_KEY] }),
    });
};

export const useDeleteConsoleHoliday = (): UseMutationResult<any, Error, { holiday_no: number }> => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ holiday_no }: { holiday_no: number }) => {
            const url = enum_api_uri.delete_console_holiday.replace(":holiday_no", String(holiday_no));
            try {
                const response = await axiosInstance.delete(url);
                return response.data;
            } catch (error: any) {
                throw toError(error, "삭제에 실패했습니다.");
            }
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: [HOLIDAYS_KEY] }),
    });
};

// 삭제된 공휴일을 원래 이름·출처(공휴일)로 복원
export const useRestoreConsoleHoliday = (): UseMutationResult<any, Error, { holiday_no: number }> => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ holiday_no }: { holiday_no: number }) => {
            const url = enum_api_uri.post_console_holiday_restore.replace(":holiday_no", String(holiday_no));
            try {
                const response = await axiosInstance.post(url);
                return response.data;
            } catch (error: any) {
                throw toError(error, "복원에 실패했습니다.");
            }
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: [HOLIDAYS_KEY] }),
    });
};

export const useSyncConsoleHoliday = (): UseMutationResult<HolidaySyncResult, Error, { year: number }> => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (body: { year: number }) => {
            try {
                const response = await axiosInstance.post(enum_api_uri.post_console_holiday_sync, body);
                return response.data.data;
            } catch (error: any) {
                throw toError(error, "공휴일을 불러오지 못했습니다.");
            }
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: [HOLIDAYS_KEY] }),
    });
};

export const usePutConsoleHolidayConfig = (): UseMutationResult<any, Error, { default_end_time: string }> => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (body: { default_end_time: string }) => {
            try {
                const response = await axiosInstance.put(enum_api_uri.put_console_holiday_config, body);
                return response.data;
            } catch (error: any) {
                throw toError(error, "저장에 실패했습니다.");
            }
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: [HOLIDAYS_KEY] }),
    });
};
